import React, { useEffect, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";

import { Dumpster } from "../../../../models";
import { dumpsterSizeToText } from "../../../../models/Order";

import * as simpleMapStyle from "../../../Schedules/components/MapView/simpleStyle.json";

import "./DumpsterMap.scss";

// Shared Google Maps config with the fleet-optimization / driver-location maps.
const GOOGLE_MAPS_KEY = "AIzaSyCkRYkFYLEOcaEbwmSYJXaQ2jlIttKkl8M";
const MAPS_VERSION = "3.58.8";
// Western PA default, matching the fleet-optimization map.
const DEFAULT_CENTER = { lat: 40.40016, lng: -80.04932 };
const DEFAULT_ZOOM = 10;

// hasCoords guards against order-located dumpsters whose order has no geocoded
// position (lat/lng absent or an unset 0,0), which would otherwise pin to the
// Gulf of Guinea.
function hasCoords(d: Dumpster): boolean {
  return (
    typeof d.orderLatitude === "number" &&
    typeof d.orderLongitude === "number" &&
    !(d.orderLatitude === 0 && d.orderLongitude === 0)
  );
}

// dotNumber pulls the "#N" number out of a label like "Dumpster #12" so it fits
// inside a dot. Falls back to the full label if there's no number.
function dotNumber(label: string): string {
  const m = label.match(/#\s*(\w+)/);
  return m ? m[1] : label;
}

interface DumpsterPinProps {
  lat: number;
  lng: number;
  dumpster: Dumpster;
}

function DumpsterPin({ dumpster }: DumpsterPinProps) {
  const [hovered, setHovered] = useState(false);

  const cityZip = [dumpster.currentCity, dumpster.currentZip]
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className="dumpster-marker"
      data-testid="dumpster-marker"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="dot">{dotNumber(dumpster.label)}</div>
      {hovered && (
        <div className="popup">
          <div className="popup-label">{dumpster.customerName || "Order"}</div>
          {dumpster.currentAddress && (
            <div className="popup-row">{dumpster.currentAddress}</div>
          )}
          {cityZip && <div className="popup-row">{cityZip}</div>}
          <div className="popup-row muted">
            {dumpsterSizeToText[dumpster.dumpsterSize]} · {dumpster.label}
          </div>
        </div>
      )}
    </div>
  );
}

export interface DumpsterMapProps {
  dumpsters: Dumpster[];
}

export function DumpsterMap({ dumpsters }: DumpsterMapProps) {
  const mapApi = useRef<{ map: any; maps: any } | null>(null);
  const located = React.useMemo(() => dumpsters.filter(hasCoords), [dumpsters]);

  const fit = () => {
    if (!mapApi.current || located.length === 0) return;
    const { map, maps } = mapApi.current;
    if (located.length === 1) {
      map.setCenter({
        lat: located[0].orderLatitude as number,
        lng: located[0].orderLongitude as number,
      });
      map.setZoom(13);
      return;
    }
    const bounds = new maps.LatLngBounds();
    located.forEach((d) =>
      bounds.extend(
        new maps.LatLng(d.orderLatitude as number, d.orderLongitude as number)
      )
    );
    // Minimal padding (px) => tightest possible zoom that still fits every pin.
    map.fitBounds(bounds, 10);
  };

  useEffect(() => {
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [located.length]);

  return (
    <div className="dumpster-map-component" data-testid="dumpster-map">
      {located.length === 0 && (
        <div className="no-location" data-testid="dumpster-map-empty">
          No delivered dumpsters with a mapped location.
        </div>
      )}
      <GoogleMapReact
        bootstrapURLKeys={{ key: GOOGLE_MAPS_KEY, version: MAPS_VERSION }}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        options={{ styles: simpleMapStyle }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          mapApi.current = { map, maps };
          fit();
        }}
      >
        {located.map((d) => (
          <DumpsterPin
            key={d.id}
            lat={d.orderLatitude as number}
            lng={d.orderLongitude as number}
            dumpster={d}
          />
        ))}
      </GoogleMapReact>
    </div>
  );
}
